import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  share,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  MapNoteModelLike,
  MapNoteType,
} from 'app/shared/models/map-note.model';
import { MainMapService } from '../../main-map.service';
import { NotesService } from '../../notes.service';
import {
  SymbolConfigLike,
  LineSymbolConfigLike,
  PolygonSymbolConfigLike,
  PointSymbolConfigLike,
  TextSymbolConfigLike,
  MapSymbolService,
} from '../../../../services/map-symbol.service';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import Draw from '@arcgis/core/views/draw/Draw';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { GeometryUtils } from 'app/shared/utils/geometry.utils';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import Sketch from '@arcgis/core/widgets/Sketch';
import { ColorUtils } from 'app/shared/utils/color.utils';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

enum DrawMode {
  Click = 'click',
  Freehand = 'freehand',
  Hybrid = 'hybrid',
}

enum EditorType {
  Point,
  Line,
  Polygon,
  Text,
}

interface NoteTypeItemLike {
  name: string;
  type: MapNoteType;
  editorType: EditorType;
  mode: DrawMode;
  icon: string;
  active?: boolean;
}

const defaultSymbolOpacity = 0.3;
const defaultPointSize = 12;
const defaultPointStyle: PointSymbolConfigLike['pointStyle'] = 'circle';
const defaultOutlineStyle: LineSymbolConfigLike['outlineStyle'] = 'solid';
const defaultOutlineWidth = 2;
const defaultOutlineColor = '#ff8000';
const defaultFillColor = ColorUtils.alpha(defaultOutlineColor, 0.3).hex('rgb');
const defaultFontSize = 12;
const defaultFontColor = '#000000';

@Component({
  selector: 'app-notes',
  templateUrl: 'notes.component.html',
  styleUrls: ['notes.component.scss'],
})
export class NotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;

  @Input()
  drawNoteGraphicsLayer: __esri.GraphicsLayer;

  private mapSketch: __esri.Sketch;

  noteTypeItemList: NoteTypeItemLike[];
  noteTypeItemSelected: NoteTypeItemLike;

  get noteTypeSelected() {
    return this.noteTypeItemSelected?.type;
  }

  get noteDrawModeSelected() {
    return this.noteTypeItemSelected?.mode;
  }

  get noteEditorTypeSelected() {
    return this.noteTypeItemSelected?.editorType;
  }

  editorTypes = EditorType;

  formGroup: FormGroup<any>;

  get isEditingActive() {
    return !!this.holderToEdit;
  }

  pointStyleList: CodedValueItem<PointSymbolConfigLike['pointStyle']>[] = [
    {
      value: 'circle',
      label: this.translationService.getCurrentLanguage().circle,
    },
    {
      value: 'square',
      label: this.translationService.getCurrentLanguage().square,
    },
    {
      value: 'diamond',
      label: this.translationService.getCurrentLanguage().diamond,
    },
    {
      value: 'cross',
      label: this.translationService.getCurrentLanguage().cross,
    },
    {
      value: 'x',
      label: 'X',
    },
  ];

  lineStyleList: CodedValueItem<LineSymbolConfigLike['outlineStyle']>[] = [
    {
      value: 'solid',
      label: this.translationService.getCurrentLanguage().solid,
    },
    {
      value: 'dash',
      label: this.translationService.getCurrentLanguage().dash,
    },
    {
      value: 'dot',
      label: this.translationService.getCurrentLanguage().dot,
    },
    {
      value: 'dash-dot',
      label: this.translationService.getCurrentLanguage()['dash-dot'],
    },
    {
      value: 'long-dash-dot-dot',
      label: this.translationService.getCurrentLanguage()['long-dash-dot-dot'],
    },
  ];

  private _noteTypeItemSelectedSubject = new Subject<NoteTypeItemLike>();
  private _startDrawSubject = new BehaviorSubject<boolean>(false);

  private holderToEdit: {
    note: MapNoteModelLike;
    graphic: __esri.Graphic;
  };
  private _startEditingSubject = new BehaviorSubject<boolean>(false);
  private _applyChangesSubject = new Subject<void>();
  private _deleteSubject = new Subject<void>();
  private _cancelSubject = new Subject<void>();

  drawingActiveObservable = this._startDrawSubject.asObservable();
  editingActiveObservable = this._startEditingSubject
    .asObservable()
    .pipe(distinctUntilChanged(), debounceTime(20), share());

  private mapViewSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private notesService: NotesService,
    private mapSymbolService: MapSymbolService,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.noteTypeItemList = [
      {
        name: this.translationService.getCurrentLanguage().point,
        type: MapNoteType.Point,
        editorType: EditorType.Point,
        mode: DrawMode.Click,
        icon: 'assets/icons/point.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().line,
        type: MapNoteType.Line,
        editorType: EditorType.Line,
        mode: DrawMode.Click,
        icon: 'assets/icons/line.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().polyline,
        type: MapNoteType.Polyline,
        editorType: EditorType.Line,
        mode: DrawMode.Click,
        icon: 'assets/icons/polyline.svg',
      },
      {
        name: this.translationService.getCurrentLanguage()['freehand-polyline'],
        type: MapNoteType.FreePolyline,
        editorType: EditorType.Line,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/polyline-free-hands.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().arrow,
        type: MapNoteType.Arrow,
        editorType: EditorType.Polygon,
        mode: DrawMode.Click,
        icon: 'assets/icons/arrow-top-right.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().triangle,
        type: MapNoteType.Triangle,
        editorType: EditorType.Polygon,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/triangle.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().rectangle,
        type: MapNoteType.Rectangle,
        editorType: EditorType.Polygon,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/rectangle.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().circle,
        type: MapNoteType.Circle,
        editorType: EditorType.Polygon,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/circle.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().ellipse,
        type: MapNoteType.Ellipse,
        editorType: EditorType.Polygon,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/ellipse.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().polygon,
        type: MapNoteType.Polygon,
        editorType: EditorType.Polygon,
        mode: DrawMode.Click,
        icon: 'assets/icons/polygon.svg',
      },
      {
        name: this.translationService.getCurrentLanguage()['freehand-polygon'],
        type: MapNoteType.FreePolygon,
        editorType: EditorType.Polygon,
        mode: DrawMode.Freehand,
        icon: 'assets/icons/polygon-free-hands.svg',
      },
      {
        name: this.translationService.getCurrentLanguage().text,
        type: MapNoteType.Text,
        editorType: EditorType.Text,
        mode: DrawMode.Click,
        icon: 'assets/icons/letter-a.svg',
      },
    ];

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.drawNoteGraphicsLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();
  }

  onSelectNoteTypeClick(item: NoteTypeItemLike) {
    this.selectNoteType(item.type);
  }

  onBtnStartDrawClick() {
    this._startDrawSubject.next(true);
  }

  onBtnApplyChangesClick() {
    if (!this.isEditingActive) {
      return;
    }

    this._applyChangesSubject.next();
  }

  onBtnCancelDrawClick() {
    if (!this.isEditingActive) {
      return;
    }

    this._cancelSubject.next();
  }

  onBtnDeleteClick() {
    if (!this.isEditingActive) {
      return;
    }

    this._deleteSubject.next();
  }

  onBtnDeleteAllClick() {
    this._cancelSubject.next();
    this.notesService.removeAllNotes();
  }

  shouldShowField(fieldName: string): boolean {
    return !!this.formGroup?.contains(fieldName);
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView || !this.drawNoteGraphicsLayer) {
      return;
    }

    const subscription = new Subscription();

    this.mapSketch = new Sketch({
      view: this.mapView,
      layer: this.drawNoteGraphicsLayer,
      creationMode: 'single',
    });

    subscription.add(
      this._noteTypeItemSelectedSubject
        .pipe(
          switchMap((item) => {
            this.handleFormGroup(item?.type);

            if (!item) {
              return EMPTY;
            }

            return this.getStartDrawObservable();
          })
        )
        .subscribe()
    );

    subscription.add(this.getStartEditingObservable().subscribe());

    subscription.add(() => {
      this.mapSketch.cancel();
      this.mapSketch.destroy();
      this.mapSketch = null;
    });

    this.mapViewSubscription = subscription;
  }

  private handleFormGroup(type: MapNoteType) {
    if (!type) {
      this.formGroup = null;
      return;
    }

    const formGroup: FormGroup = this.formBuilder.group({
      opacity:
        this.formBuilder.control<SymbolConfigLike['opacity']>(
          defaultSymbolOpacity
        ),
    });

    switch (type) {
      case MapNoteType.Point:
        formGroup.addControl(
          'pointStyle',
          this.formBuilder.control<PointSymbolConfigLike['pointStyle']>(
            defaultPointStyle
          )
        );
        formGroup.addControl(
          'fillColor',
          this.formBuilder.control<PointSymbolConfigLike['fillColor']>(
            defaultFillColor
          )
        );
        formGroup.addControl(
          'size',
          this.formBuilder.control<PointSymbolConfigLike['size']>(
            defaultPointSize
          )
        );
        break;
      case MapNoteType.Line:
      case MapNoteType.Polyline:
      case MapNoteType.FreePolyline:
        formGroup.addControl(
          'outlineColor',
          this.formBuilder.control<LineSymbolConfigLike['outlineColor']>(
            defaultOutlineColor
          )
        );
        formGroup.addControl(
          'outlineWidth',
          this.formBuilder.control<LineSymbolConfigLike['outlineWidth']>(
            defaultOutlineWidth
          )
        );
        formGroup.addControl(
          'outlineStyle',
          this.formBuilder.control<LineSymbolConfigLike['outlineStyle']>(
            defaultOutlineStyle
          )
        );
        break;
      case MapNoteType.Arrow:
      case MapNoteType.Triangle:
      case MapNoteType.Rectangle:
      case MapNoteType.Circle:
      case MapNoteType.Ellipse:
      case MapNoteType.Polygon:
      case MapNoteType.FreePolygon:
        formGroup.addControl(
          'fillColor',
          this.formBuilder.control<PolygonSymbolConfigLike['fillColor']>(
            defaultFillColor
          )
        );
        formGroup.addControl(
          'outlineColor',
          this.formBuilder.control<PolygonSymbolConfigLike['outlineColor']>(
            defaultOutlineColor
          )
        );
        formGroup.addControl(
          'outlineWidth',
          this.formBuilder.control<PolygonSymbolConfigLike['outlineWidth']>(
            defaultOutlineWidth
          )
        );
        break;
      case MapNoteType.Text:
        formGroup.addControl(
          'text',
          this.formBuilder.control<TextSymbolConfigLike['text']>(
            this.translationService.getCurrentLanguage().example
          )
        );
        formGroup.addControl(
          'fontSize',
          this.formBuilder.control<TextSymbolConfigLike['fontSize']>(
            defaultFontSize
          )
        );
        formGroup.addControl(
          'fontColor',
          this.formBuilder.control<TextSymbolConfigLike['fontColor']>(
            defaultFontColor
          )
        );
        break;
    }

    if (this.holderToEdit?.note?.symbolParams) {
      formGroup.reset(this.holderToEdit.note.symbolParams);
    }

    this.formGroup = formGroup;
  }

  private getStartDrawObservable() {
    return this._startDrawSubject.pipe(
      switchMap((active) => {
        if (!active) {
          return EMPTY;
        }

        let observable: Observable<{
          symbolParams: any;
          graphic: __esri.Graphic;
        }>;

        switch (this.noteTypeSelected) {
          case MapNoteType.Point:
          case MapNoteType.Text:
            observable = this.getDrawingOperationSketchObservable('point');
            break;
          case MapNoteType.Line:
          case MapNoteType.Polyline:
          case MapNoteType.FreePolyline:
            observable = this.getDrawingOperationSketchObservable('polyline');
            break;
          case MapNoteType.Rectangle:
            observable = this.getDrawingOperationSketchObservable('rectangle');
            break;
          case MapNoteType.Circle:
            observable = this.getDrawingOperationSketchObservable('circle');
            break;
          case MapNoteType.Polygon:
          case MapNoteType.FreePolygon:
            observable = this.getDrawingOperationSketchObservable('polygon');
            break;
          case MapNoteType.Arrow:
          case MapNoteType.Triangle:
          case MapNoteType.Ellipse:
            observable = this.getDrawingOperationWidgetObservable();
            break;
        }

        return observable.pipe(
          finalize(() => {
            this._startDrawSubject.next(false);
          }),
          tap(({ symbolParams, graphic }) => {
            const note: MapNoteModelLike = {
              type: this.noteTypeSelected,
              symbolParams,
              geometry: graphic.geometry,
            };

            this.notesService.addNoteAndGraphic(note, graphic);
          })
        );
      })
    );
  }

  private getDrawingOperationSketchObservable(
    type: 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle'
  ) {
    return new Observable<{ symbolParams: any; graphic: __esri.Graphic }>(
      (subscriber) => {
        this.mapView.focus();

        const symbolParams = this.formGroup.getRawValue();
        this.refreshSymbol(symbolParams);

        subscriber.add(
          EsriUtils.on(this.mapSketch, 'create').subscribe(
            (e: __esri.SketchCreateEvent) => {
              switch (e.state) {
                case 'cancel':
                  subscriber.complete();
                  break;
                case 'start':
                case 'active':
                case 'complete':
                  if (
                    this.noteTypeSelected === MapNoteType.Line &&
                    e.state === 'active' &&
                    e.toolEventInfo.type === 'vertex-add'
                  ) {
                    this.mapSketch.complete();
                  }

                  if (e.state === 'complete') {
                    subscriber.next({ symbolParams, graphic: e.graphic });
                    subscriber.complete();
                  }
                  break;
              }
            }
          )
        );

        this.mapSketch.create(type, { mode: this.noteDrawModeSelected });
      }
    );
  }

  private getDrawingOperationWidgetObservable() {
    return new Observable<{ symbolParams: any; graphic: __esri.Graphic }>(
      (subscriber) => {
        this.mapView.focus();

        const mapDraw = new Draw({
          view: this.mapView,
        });
        const action = mapDraw.create('ellipse');

        const symbolParams = this.formGroup.getRawValue();
        const symbol = this.refreshSymbol(symbolParams);
        let graphic: __esri.Graphic;

        const drawCompleteObservable = EsriUtils.on(
          action,
          'draw-complete'
        ).pipe(
          first(),
          tap(() => {
            if (graphic) {
              subscriber.next({ symbolParams, graphic });
            }
          })
        );

        const cancelObservable = EsriUtils.on(this.mapView, 'key-down').pipe(
          first((e) => {
            return e.key === 'Escape';
          }),
          tap(() => {
            if (graphic) {
              this.drawNoteGraphicsLayer.remove(graphic);
            }
          })
        );

        subscriber.add(
          EsriUtils.on(action, 'cursor-update')
            .pipe(
              map(
                (event: __esri.SegmentDrawActionCursorUpdateEvent) =>
                  event.vertices
              ),
              filter((vertices) => vertices.length === 2),
              map((vertices) => {
                switch (this.noteTypeSelected) {
                  case MapNoteType.Arrow:
                    const width = EsriUtils.convertPxToDistanceXCoords(
                      this.mapView,
                      30
                    );
                    return GeometryUtils.createArrowByVertices(vertices, width);
                  case MapNoteType.Triangle:
                    return GeometryUtils.createTriangleByVertices(
                      vertices,
                      true
                    );
                  case MapNoteType.Ellipse:
                  default:
                    return GeometryUtils.createEllipseByVertices(vertices);
                }
              }),
              tap((vertices) => {
                if (!graphic) {
                  graphic = new Graphic({
                    symbol: symbol as any,
                  });

                  this.drawNoteGraphicsLayer.add(graphic);
                }

                graphic.geometry = new Polygon({
                  rings: [vertices],
                  spatialReference: this.mapView.spatialReference,
                });
              }),
              finalize(() => {
                subscriber.complete();
              }),
              takeUntil(
                merge(drawCompleteObservable, cancelObservable).pipe(first())
              )
            )
            .subscribe()
        );

        subscriber.add(() => {
          mapDraw.reset();
          action.destroy();
          mapDraw.destroy();
        });
      }
    );
  }

  private getStartEditingObservable() {
    return EsriUtils.on<__esri.SketchUpdateEvent>(
      this.mapSketch,
      'update'
    ).pipe(
      filter((e) => e.state === 'start'),
      switchMap((e) => {
        const graphic = e.graphics?.[0];
        const note = this.notesService.findNoteByKey(graphic?.attributes?.key);

        if (!note) {
          return EMPTY;
        }

        return new Observable((subscriber) => {
          this.holderToEdit = { note, graphic };
          this._startEditingSubject.next(true);

          subscriber.add(
            merge(
              this.getEditingOperationObservable(this.holderToEdit),
              this._cancelSubject
            )
              .pipe(first())
              .subscribe(() => {
                subscriber.complete();
              })
          );

          subscriber.add(() => {
            this.holderToEdit = null;
            this._startEditingSubject.next(false);
          });
        });
      })
    );
  }

  private getEditingOperationObservable(holder: {
    note: MapNoteModelLike;
    graphic: __esri.Graphic;
  }) {
    return new Observable<void>((subscriber) => {
      this.selectNoteType(holder.note.type);

      subscriber.add(
        this._applyChangesSubject
          .pipe(map(() => this.formGroup?.getRawValue()))
          .subscribe((changes) => {
            holder.note.symbolParams = changes;
            this.notesService.updateNote(holder.note);
          })
      );

      subscriber.add(
        merge(
          EsriUtils.on(this.mapSketch, 'delete'),
          this._deleteSubject
        ).subscribe(() => {
          holder.graphic = null;
          this.notesService.removeNote(holder.note);

          subscriber.next();
          subscriber.complete();
        })
      );

      subscriber.add(
        EsriUtils.on(this.mapSketch, 'update').subscribe(
          (e: __esri.SketchUpdateEvent) => {
            switch (e.state) {
              case 'active':
                if (holder.graphic) {
                  holder.note.geometry = holder.graphic.geometry;
                }
                break;
              case 'complete':
                if (holder.graphic) {
                  holder.note.geometry = holder.graphic.geometry;

                  this.notesService.updateNote(holder.note);
                  subscriber.next();
                  subscriber.complete();
                }
                break;
            }
          }
        )
      );

      subscriber.add(() => {
        this.mapSketch.cancel();
      });
    });
  }

  private refreshSymbol(symbolParams: any = this.formGroup?.getRawValue()) {
    let symbol: __esri.Symbol;
    switch (this.noteTypeSelected) {
      case MapNoteType.Point:
        symbol = this.mapSymbolService.createPointSymbol(symbolParams);
        if (this.mapSketch) {
          this.mapSketch.viewModel.pointSymbol = symbol as any;
        }
        break;
      case MapNoteType.Line:
      case MapNoteType.Polyline:
      case MapNoteType.FreePolyline:
        symbol = this.mapSymbolService.createLineSymbol(symbolParams);
        if (this.mapSketch) {
          this.mapSketch.viewModel.polylineSymbol = symbol as any;
        }
        break;
      case MapNoteType.Polygon:
      case MapNoteType.FreePolygon:
      case MapNoteType.Circle:
      case MapNoteType.Rectangle:
      case MapNoteType.Arrow:
      case MapNoteType.Triangle:
      case MapNoteType.Ellipse:
        symbol = this.mapSymbolService.createPolygonSymbol(symbolParams);
        if (this.mapSketch) {
          this.mapSketch.viewModel.polygonSymbol = symbol as any;
        }
        break;
      case MapNoteType.Text:
        symbol = this.mapSymbolService.createTextSymbol(symbolParams);
        if (this.mapSketch) {
          this.mapSketch.viewModel.pointSymbol = symbol as any;
        }
        break;
    }

    return symbol;
  }

  private selectNoteType(noteType: MapNoteType) {
    const item = this.noteTypeItemList.find((x) => x.type === noteType) ?? null;

    if (this.noteTypeItemSelected) {
      this.noteTypeItemSelected.active = false;
    }

    if (item) {
      item.active = true;
    }
    this.noteTypeItemSelected = item;
    this._noteTypeItemSelectedSubject.next(item);
  }
}
